import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  Alert, 
  CircularProgress, 
  Badge,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import { 
  Upload, 
  Download, 
  Refresh, 
  FileUpload, 
  AccountBalance,
  Business,
  CheckCircle,
  ErrorOutline
} from '@mui/icons-material';
import { TreasuryDataGrid } from '../DataGrid/TreasuryDataGrid';
import { GridColDef } from '@mui/x-data-grid';

interface CuentaProyecto {
  id: string;
  'Proyecto': string;
  'Codigo Proyecto': string;
  'Gerencia': string;
  'No. Cuenta': string;
  'Banco': string;
  'Centralizadora': string;
}

interface CuentaCentralizadora {
  id: string;
  'No. Cuenta': string;
  'Banco': string;
}

interface ActualizarCuentasResultProps {
  initialData?: {
    cuentasProyecto?: CuentaProyecto[];
    cuentasCentralizadoras?: CuentaCentralizadora[];
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cuentas-tabpanel-${index}`}
      aria-labelledby={`cuentas-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const ActualizarCuentasResult: React.FC<ActualizarCuentasResultProps> = ({ 
  initialData = {} 
}) => {
  // State management
  const [currentTab, setCurrentTab] = useState(0);
  const [cuentasProyecto, setCuentasProyecto] = useState<CuentaProyecto[]>(initialData.cuentasProyecto || []);
  const [cuentasCentralizadoras, setCuentasCentralizadoras] = useState<CuentaCentralizadora[]>(initialData.cuentasCentralizadoras || []);
  const [isLoadingProyecto, setIsLoadingProyecto] = useState(false);
  const [isLoadingCentralizadoras, setIsLoadingCentralizadoras] = useState(false);
  const [uploadDialog, setUploadDialog] = useState({ open: false, type: '' });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);

  // Load initial data
  useEffect(() => {
    if (cuentasProyecto.length === 0) {
      loadCuentasProyecto();
    }
  }, []);

  const loadCuentasProyecto = async () => {
    setIsLoadingProyecto(true);
    try {
      const response = await fetch('/obtener-cuentas-actuales');
      if (!response.ok) throw new Error('Error al cargar cuentas por proyecto');
      const data = await response.json();
      
      // Transform data to ensure proper IDs
      const transformedData = data.map((item: any, index: number) => ({
        ...item,
        id: `proyecto_${index}_${item['No. Cuenta'] || index}`,
        'Centralizadora': item['Centralizadora'] === true || item['Centralizadora'] === 'true' || 
                         item['Centralizadora'] === 1 || item['Centralizadora'] === 'SI' ? 'SI' : 'NO'
      }));
      
      setCuentasProyecto(transformedData);
    } catch (error) {
      console.error('Error loading cuentas proyecto:', error);
      setAlert({ type: 'error', message: 'Error al cargar las cuentas por proyecto' });
    } finally {
      setIsLoadingProyecto(false);
    }
  };

  const loadCuentasCentralizadoras = async () => {
    setIsLoadingCentralizadoras(true);
    try {
      const response = await fetch('/obtener-cuentas-centralizadoras-actuales');
      if (!response.ok) throw new Error('Error al cargar cuentas centralizadoras');
      const data = await response.json();
      
      // Transform data to ensure proper IDs
      const transformedData = data.map((item: any, index: number) => ({
        ...item,
        id: `centralizadora_${index}_${item['No. Cuenta'] || index}`
      }));
      
      setCuentasCentralizadoras(transformedData);
    } catch (error) {
      console.error('Error loading cuentas centralizadoras:', error);
      setAlert({ type: 'error', message: 'Error al cargar las cuentas centralizadoras' });
    } finally {
      setIsLoadingCentralizadoras(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    // Load centralizadoras data when switching to that tab
    if (newValue === 1 && cuentasCentralizadoras.length === 0) {
      loadCuentasCentralizadoras();
    }
  };

  const handleUploadClick = (type: 'proyecto' | 'centralizadoras') => {
    setUploadDialog({ open: true, type });
    setSelectedFile(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('form_type', uploadDialog.type === 'proyecto' ? 'cuentas_proyecto' : 'cuentas_centralizadoras');

      const response = await fetch('/actualizar-cuentas', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Error al actualizar cuentas');

      setAlert({ type: 'success', message: 'Cuentas actualizadas exitosamente' });
      setUploadDialog({ open: false, type: '' });
      setSelectedFile(null);
      
      // Reload appropriate data
      if (uploadDialog.type === 'proyecto') {
        loadCuentasProyecto();
      } else {
        loadCuentasCentralizadoras();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setAlert({ type: 'error', message: 'Error al actualizar las cuentas' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open('/download-cuentas-excel', '_blank');
  };

  const handleRefresh = () => {
    if (currentTab === 0) {
      loadCuentasProyecto();
    } else {
      loadCuentasCentralizadoras();
    }
  };

  // Column definitions for Cuentas por Proyecto
  const cuentasProyectoColumns: GridColDef[] = [
    {
      field: 'Proyecto',
      headerName: 'Proyecto',
      width: 200,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Codigo Proyecto',
      headerName: 'Código Proyecto',
      width: 180,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Gerencia',
      headerName: 'Gerencia',
      width: 150,
      filterable: true,
      sortable: true,
    },
    {
      field: 'No. Cuenta',
      headerName: 'No. Cuenta',
      width: 150,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Banco',
      headerName: 'Banco',
      width: 150,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Centralizadora',
      headerName: 'Centralizadora',
      width: 120,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'SI' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
  ];

  // Column definitions for Cuentas Centralizadoras
  const cuentasCentralizadorasColumns: GridColDef[] = [
    {
      field: 'No. Cuenta',
      headerName: 'No. Cuenta',
      width: 200,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Banco',
      headerName: 'Banco',
      width: 200,
      filterable: true,
      sortable: true,
    },
  ];

  const closeAlert = () => setAlert(null);

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* Alert */}
      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={closeAlert}
          sx={{ mb: 2 }}
          icon={alert.type === 'success' ? <CheckCircle /> : <ErrorOutline />}
        >
          {alert.message}
        </Alert>
      )}

      {/* Main Card */}
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <AccountBalance color="success" />
              <Typography variant="h5" component="div">
                Actualizar Cuentas Bancarias
              </Typography>
            </Box>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadTemplate}
                size="small"
              >
                Descargar Plantilla
              </Button>
              <Button
                variant="outlined"
                startIcon={(currentTab === 0 ? isLoadingProyecto : isLoadingCentralizadoras) ? <CircularProgress size={16} /> : <Refresh />}
                onClick={handleRefresh}
                size="small"
                disabled={currentTab === 0 ? isLoadingProyecto : isLoadingCentralizadoras}
              >
                {(currentTab === 0 ? isLoadingProyecto : isLoadingCentralizadoras) ? 'Cargando...' : 'Actualizar'}
              </Button>
            </Stack>
          }
          sx={{ 
            backgroundColor: 'primary.main',
            color: 'white',
            '& .MuiCardHeader-action': { 
              '& .MuiButton-outlined': {
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }
            }
          }}
        />
        <CardContent>
          {/* Tabs */}
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider', 
              mb: 2,
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
              },
              '& .MuiTab-root': {
                color: 'text.secondary',
              },
              '& .MuiTab-root.Mui-selected': {
                color: 'primary.main',
              }
            }}
          >
            <Tab
              icon={<Business />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  Cuentas por Proyecto
                  <Badge badgeContent={cuentasProyecto.length} color="primary" />
                </Box>
              }
              id="cuentas-tab-0"
              aria-controls="cuentas-tabpanel-0"
            />
            <Tab
              icon={<AccountBalance />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  Cuentas Centralizadoras
                  <Badge badgeContent={cuentasCentralizadoras.length} color="primary" />
                </Box>
              }
              id="cuentas-tab-1"
              aria-controls="cuentas-tabpanel-1"
            />
          </Tabs>

          {/* Tab Panel 0: Cuentas por Proyecto */}
          <TabPanel value={currentTab} index={0}>
            <Box sx={{ mb: 3 }}>
              {/* Upload Section */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" gutterBottom>
                      <Upload sx={{ mr: 1 }} />
                      Cargar archivo para Cuentas por Proyecto
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Archivo Excel (.xlsx) con las columnas requeridas
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<FileUpload />}
                      onClick={() => handleUploadClick('proyecto')}
                      fullWidth
                    >
                      Actualizar Cuentas por Proyecto
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* Data Grid */}
              <TreasuryDataGrid
                rows={cuentasProyecto}
                columns={cuentasProyectoColumns}
                loading={isLoadingProyecto}
                showToolbar={true}
                showExport={true}
                showRefresh={true}
                title="Cuentas por Proyecto"
                tableId="cuentas-proyecto"
                onRefresh={loadCuentasProyecto}
                exportOptions={{
                  filename: 'cuentas_proyecto',
                  includeHeader: true,
                  includeFooter: true
                }}
                sx={{ height: 400 }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                density="compact"
                filterMode="client"
                sortingMode="client"
                paginationMode="client"
              />
            </Box>
          </TabPanel>

          {/* Tab Panel 1: Cuentas Centralizadoras */}
          <TabPanel value={currentTab} index={1}>
            <Box sx={{ mb: 3 }}>
              {/* Upload Section */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" gutterBottom>
                      <Upload sx={{ mr: 1 }} />
                      Cargar archivo para Cuentas Centralizadoras
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Archivo Excel (.xlsx) con las columnas requeridas
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<FileUpload />}
                      onClick={() => handleUploadClick('centralizadoras')}
                      fullWidth
                    >
                      Actualizar Cuentas Centralizadoras
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* Data Grid */}
              <TreasuryDataGrid
                rows={cuentasCentralizadoras}
                columns={cuentasCentralizadorasColumns}
                loading={isLoadingCentralizadoras}
                showToolbar={true}
                showExport={true}
                showRefresh={true}
                title="Cuentas Centralizadoras"
                tableId="cuentas-centralizadoras"
                onRefresh={loadCuentasCentralizadoras}
                exportOptions={{
                  filename: 'cuentas_centralizadoras',
                  includeHeader: true,
                  includeFooter: true
                }}
                sx={{ height: 400 }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                density="compact"
                filterMode="client"
                sortingMode="client"
                paginationMode="client"
              />
            </Box>
          </TabPanel>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialog.open}
        onClose={() => setUploadDialog({ open: false, type: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <FileUpload />
            Cargar Archivo Excel
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Seleccionar archivo Excel para actualizar{' '}
              {uploadDialog.type === 'proyecto' ? 'Cuentas por Proyecto' : 'Cuentas Centralizadoras'}
            </Typography>
            <TextField
              type="file"
              fullWidth
              variant="outlined"
              onChange={handleFileSelect}
              inputProps={{
                accept: '.xlsx,.xls',
              }}
              sx={{ mt: 2 }}
            />
            {selectedFile && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Archivo seleccionado:</strong> {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tamaño: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUploadDialog({ open: false, type: '' })}
            disabled={isUploading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            color="success"
            disabled={!selectedFile || isUploading}
            startIcon={isUploading ? <CircularProgress size={20} /> : <Upload />}
          >
            {isUploading ? 'Cargando...' : 'Cargar Archivo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActualizarCuentasResult;